import { useNavigate } from 'react-router-dom';
import { CheckCheck } from 'lucide-react';
import {
  Button,
  EmptyState,
  ErrorState,
  Skeleton,
  SubBar,
} from '@/design/components';
import { cn } from '@/lib/cn';
import { useMe } from '@/auth/useMe';
import { formatDateTime } from '@/lib/dates';
import {
  useNotificationMutations,
  useNotifications,
  useNotificationsRealtime,
  type Notification,
} from './api';

/**
 * מרכז ההתראות. כל התראה נשמרת כאן תמיד, גם כשה Push של אותו סוג
 * כבוי אצל העובד (PRD 5.14.3).
 */
export function NotificationsPage() {
  const { me } = useMe();
  const navigate = useNavigate();
  const { data, isPending, isError, refetch } = useNotifications();
  const { markRead, markAllRead } = useNotificationMutations();
  useNotificationsRealtime(me?.employeeId);

  const unread = (data ?? []).filter((item) => item.read_at === null);

  function open(item: Notification) {
    if (item.read_at === null) markRead.mutate(item.id);
    if (item.link) navigate(item.link);
  }

  return (
    <div className="grid content-start gap-4">
      <SubBar
        title="התראות"
        subtitle={unread.length > 0 ? `${unread.length} חדשות` : undefined}
      />

      <div className="grid gap-3 px-4 pb-8">
        {unread.length > 0 && (
          <div>
            <Button variant="secondary" onClick={() => markAllRead.mutate()}>
              <CheckCheck size={20} strokeWidth={1.75} aria-hidden="true" />
              סימון הכל כנקרא
            </Button>
          </div>
        )}

        {isPending && (
          <div className="grid gap-2">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        )}

        {isError && <ErrorState onRetry={() => void refetch()} />}

        {data && data.length === 0 && (
          <EmptyState title="אין התראות" description="כשיקרה משהו שחשוב לך, זה יופיע כאן." />
        )}

        <ul className="grid list-none gap-2 p-0">
          {(data ?? []).map((item) => {
            const isUnread = item.read_at === null;
            const Tag = item.link ? 'button' : 'div';

            return (
              <li key={item.id}>
                <Tag
                  {...(item.link
                    ? { type: 'button' as const, onClick: () => open(item) }
                    : {})}
                  className={cn(
                    'grid w-full gap-1 rounded-md border p-3 text-start',
                    item.link && 'cursor-pointer',
                    isUnread ? 'border-olive bg-olive-soft' : 'border-line bg-raised',
                  )}
                >
                  <span className="flex items-start justify-between gap-2">
                    <span className={cn('text-title', isUnread && 'font-medium')}>
                      {item.title}
                    </span>
                    {isUnread && (
                      <span
                        aria-label="חדש"
                        className="mt-1 size-2 shrink-0 rounded-pill bg-olive"
                      />
                    )}
                  </span>

                  {item.body && <span className="text-body-sm text-ink">{item.body}</span>}

                  <span className="text-label text-muted">
                    {formatDateTime(item.created_at)}
                  </span>
                </Tag>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
